import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveImg } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";

const CATEGORIES = ["Inverno", "Primavera", "Estate", "Autunno"];
const EMPTY = { title: "", category: "Estate", description: "", image: "", order: 0, partners: [] };

export default function AdminExperiences() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/experiences").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const open = (exp) => { setEditing(exp?.id || "new"); setForm(exp ? { ...exp, partners: exp.partners || [] } : { ...EMPTY, order: list.length }); };

  const save = async () => {
    if (!form.title) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/experiences", form);
      else await api.put(`/admin/experiences/${editing}`, form);
      toast.success("Esperienza salvata"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa esperienza?")) return;
    await api.delete(`/admin/experiences/${id}`); toast.success("Eliminata"); load();
  };

  const setPartner = (i, patch) => setForm({ ...form, partners: form.partners.map((p, idx) => idx === i ? { ...p, ...patch } : p) });
  const addPartner = () => setForm({ ...form, partners: [...form.partners, { name: "", discount: "", description: "", link: "" }] });
  const delPartner = (i) => setForm({ ...form, partners: form.partners.filter((_, idx) => idx !== i) });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl text-fir">Esperienze & Convenzionati</h1>
          <p className="text-fir-deep/60">Gestisci le esperienze mostrate sul sito e i partner con sconti dedicati.</p>
        </div>
        <button data-testid="add-experience-btn" onClick={() => open(null)} className="gold-btn px-5 py-3 rounded-xl flex items-center gap-2"><Plus size={18} /> Nuova esperienza</button>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {list.map((e) => (
          <div key={e.id} data-testid="admin-experience-row" className="bg-white rounded-2xl border border-stone overflow-hidden">
            <div className="h-36 relative">
              <img src={resolveImg(e.image) || "https://images.unsplash.com/photo-1628087236657-0cc963ad15fd?crop=entropy&cs=srgb&fm=jpg&w=400&q=60"} alt="" className="h-full w-full object-cover" />
              <span className="absolute top-2 left-2 bg-fir text-ivory text-xs px-2 py-1 rounded-full">{e.category}</span>
            </div>
            <div className="p-4">
              <h3 className="font-display text-xl text-fir">{e.title}</h3>
              <p className="text-sm text-fir-deep/60 line-clamp-2 mt-1">{e.description}</p>
              {e.partners?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {e.partners.map((p, i) => (
                    <span key={i} className="text-xs bg-gold/15 text-fir px-2 py-1 rounded-full flex items-center gap-1"><Tag size={11} />{p.name} {p.discount}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={() => open(e)} className="text-fir hover:text-gold flex items-center gap-1 text-sm"><Pencil size={14} /> Modifica</button>
                <button data-testid="delete-experience-btn" onClick={() => remove(e.id)} className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"><Trash2 size={14} /> Elimina</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-fir-deep/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-7" data-lenis-prevent onClick={(ev) => ev.stopPropagation()} data-testid="experience-form">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-3xl text-fir">{editing === "new" ? "Nuova esperienza" : "Modifica esperienza"}</h2>
              <button onClick={() => setEditing(null)}><X className="text-fir" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Titolo</label>
                <input data-testid="exp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-alpine w-full mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-fir-deep/60">Categoria / Stagione</label>
                  <select data-testid="exp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-alpine w-full mt-1">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-fir-deep/60">Ordine</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="input-alpine w-full mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Descrizione</label>
                <textarea data-testid="exp-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-alpine w-full h-20 mt-1" />
              </div>
              <ImageUploader label="Immagine esperienza" value={form.image ? [form.image] : []}
                onChange={(v) => setForm({ ...form, image: v[v.length - 1] || "" })} testid="exp-image-upload" />

              <div className="border-t border-stone pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl text-fir">Convenzionati & sconti</h3>
                  <button data-testid="add-partner-btn" onClick={addPartner} className="text-sm text-fir hover:text-gold flex items-center gap-1"><Plus size={14} /> Aggiungi partner</button>
                </div>
                <div className="space-y-3">
                  {form.partners.map((p, i) => (
                    <div key={i} data-testid="partner-row" className="bg-white rounded-xl border border-stone p-3 grid grid-cols-12 gap-2 items-center">
                      <input placeholder="Nome partner" data-testid="partner-name" value={p.name} onChange={(e) => setPartner(i, { name: e.target.value })} className="input-alpine col-span-4" />
                      <input placeholder="Sconto (es. -15%)" data-testid="partner-discount" value={p.discount} onChange={(e) => setPartner(i, { discount: e.target.value })} className="input-alpine col-span-3" />
                      <input placeholder="Descrizione" value={p.description} onChange={(e) => setPartner(i, { description: e.target.value })} className="input-alpine col-span-4" />
                      <button data-testid="delete-partner-btn" onClick={() => delPartner(i)} className="text-red-500 hover:text-red-700 col-span-1 flex justify-center"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  {form.partners.length === 0 && <p className="text-sm text-fir-deep/50">Nessun convenzionato. Aggiungine uno.</p>}
                </div>
              </div>

              <button data-testid="save-experience-btn" onClick={save} disabled={saving} className="gold-btn w-full py-3 rounded-xl flex items-center justify-center gap-2">
                {saving ? <Loader2 className="animate-spin" size={18} /> : "Salva esperienza"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
